import React, { useState, useRef, useEffect } from "react";
import "./OtpVerification.css";

import { auth, db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { generateId } from "../utils/generateId";

import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  createUserWithEmailAndPassword
} from "firebase/auth";

import { useNavigate } from "react-router-dom";

// Convert base64 data URL back to a Blob for Firebase Storage upload
async function base64ToBlob(dataUrl) {
  const res = await fetch(dataUrl);
  return res.blob();
}

export default function OtpVerification() {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [error, setError] = useState(false);
  const [lang, setLang] = useState("en");

  const navigate = useNavigate();

  const text = {
    en: {
      title: "Verify OTP", resend: "Resend OTP", verifying: "Verifying...",
      verify: "Verify", invalid: "Invalid OTP ❌", resendIn: "Resend in"
    },
    kn: {
      title: "OTP ಪರಿಶೀಲನೆ", resend: "OTP ಮರುಕಳುಹಿಸಿ", verifying: "ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ...",
      verify: "ಪರಿಶೀಲಿಸಿ", invalid: "ತಪ್ಪಾದ OTP ❌", resendIn: "ಮರುಕಳುಹಿಸಲು"
    }
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) { setError(true); return; }

    try {
      setLoading(true);
      setError(false);

      if (!window.confirmationResult) {
        alert("Session expired. Please register again.");
        navigate("/");
        return;
      }

      // Verify OTP — signs in the phone auth user
      await window.confirmationResult.confirm(enteredOtp);
      console.log("✅ OTP VERIFIED");

      // =========================
      // USER FLOW
      // =========================
      const userData = JSON.parse(localStorage.getItem("tempUser"));

      if (userData) {
        // Create email/password account so user can log in via Login.js
        const emailCred = await createUserWithEmailAndPassword(
          auth,
          userData.email,
          userData.password
        );
        const user = emailCred.user;

        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          id: userData.id,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          address: userData.address || "",
          subdistrict: userData.subdistrict || "",
          photoURL: userData.photoURL || "",
          role: "user",
          status: "registered",
          createdAt: new Date()
        });

        localStorage.setItem("user", JSON.stringify({
          uid: user.uid,
          id: userData.id,
          name: userData.name,
          phone: userData.phone,
          email: userData.email,
          photoURL: userData.photoURL || ""
        }));

        localStorage.removeItem("tempUser");
        navigate("/user-idcard");
        return;
      }

      // =========================
      // VENDOR FLOW
      // =========================
      const vendorData = JSON.parse(localStorage.getItem("vendorData"));

      if (vendorData) {
        const { form, image: imageBase64, id: vendorId } = vendorData;

        // Create vendor email/password account
        const emailCred = await createUserWithEmailAndPassword(
          auth,
          form.email,
          form.password
        );
        const user = emailCred.user;

        let imageUrl = "";
        if (imageBase64) {
          // Convert base64 data URL back to Blob for upload
          const blob = await base64ToBlob(imageBase64);
          const imageRef = ref(storage, `vendors/${vendorId}`);
          await uploadBytes(imageRef, blob);
          imageUrl = await getDownloadURL(imageRef);
        }

        await setDoc(doc(db, "vendors", user.uid), {
          id: vendorId,
          uid: user.uid,
          ...form,
          image: imageUrl,
          role: "vendor",
          paymentStatus: "pending",
          approvalStatus: "pending",
          status: "pending",
          approved: false,
          createdAt: new Date()
        });

        localStorage.removeItem("vendorData");
        navigate("/vendor-payment");
        return;
      }

      alert("Something went wrong");
      navigate("/");

    } catch (error) {
      console.error("❌ OTP ERROR:", error);
      alert(error.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const tempUser = JSON.parse(localStorage.getItem("tempUser"));
      const vendorData = JSON.parse(localStorage.getItem("vendorData"));
      const phone = tempUser?.phone || vendorData?.form?.phone;

      if (!phone) {
        alert("Phone not found");
        return;
      }

      setTimer(30);

      // Always destroy the old widget fully before creating a new one;
      // re-rendering into a container that already has a widget causes
      // auth/captcha-check-failed on production.
      try { window.recaptchaVerifier?.clear(); } catch (_) {}
      window.recaptchaVerifier = null;

      const container = document.getElementById("recaptcha-container");
      if (container) container.innerHTML = "";

      const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {}
      });
      await verifier.render();
      window.recaptchaVerifier = verifier;

      const result = await signInWithPhoneNumber(auth, "+91" + phone, verifier);
      window.confirmationResult = result;
      alert("OTP Resent ✅");
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  return (
    <div className="otp-container">
      <div className="lang-toggle">
        <button onClick={() => setLang("en")}>EN</button>
        <button onClick={() => setLang("kn")}>ಕನ್ನಡ</button>
      </div>

      <h2>{text[lang].title}</h2>

      <div className={`otp-inputs ${error ? "shake" : ""}`}>
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            maxLength="1"
            value={digit}
            ref={(el) => (inputs.current[index] = el)}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          />
        ))}
      </div>

      {error && <p className="error-text">{text[lang].invalid}</p>}

      <button className="verify-btn" onClick={handleVerify}>
        {loading ? text[lang].verifying : text[lang].verify}
      </button>

      <p className="resend-text">
        {timer > 0
          ? `${text[lang].resendIn} ${timer}s`
          : <span onClick={handleResend}>{text[lang].resend}</span>
        }
      </p>

      <div id="recaptcha-container"></div>
    </div>
  );
}
